import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type GridSizeValue = number | "auto" | "grow";

type GridSize =
  | GridSizeValue
  | {
      xs?: GridSizeValue;
      sm?: GridSizeValue;
      md?: GridSizeValue;
      lg?: GridSizeValue;
      xl?: GridSizeValue;
    };

type Props = {
  children: ReactNode;
  size?: GridSize;
  /** Vertical alignment of the field within its grid cell. */
  align?: "start" | "end" | "stretch";
  sx?: SxProps<Theme>;
};

/**
 * Grid column for a single control.
 * Children are always full-width of the column so same-row fields share one left edge.
 */
export default function AppFormField({ children, size = 12, align = "stretch", sx }: Props) {
  const custom = useAppCustomStyles();
  const innerSx =
    align === "start"
      ? custom.components.appFormField.innerStart
      : align === "end"
        ? custom.components.appFormField.innerEnd
        : custom.components.appFormField.innerStretch;

  return (
    <Grid
      size={size}
      sx={[
        {
          display: "flex",
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={[
          innerSx,
          {
            width: "100%",
            minWidth: 0,
            // Clear space for outlined / floating labels above the control border.
            pt: 0.5,
            "& > *": {
              width: "100%",
              maxWidth: "100%",
            },
            "& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root": {
              width: "100%",
              maxWidth: "100%",
            },
          },
        ]}
      >
        {children}
      </Box>
    </Grid>
  );
}
